import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";
import PropTypes from "prop-types";
import Box from "../wrappers/Box";
import Tooltip from "../wrappers/Tooltip";
import Typography from "../wrappers/Typography";

const SearchFiltersSectionsContext = createContext(null);

/** Mantém internamente a abertura das seções pertencentes ao mesmo shell de filtros. */
const SearchFiltersSectionsProvider = (pProps) => {
  const { children } = pProps;
  const [xSections, setSections] = useState({});
  const xOpenChangeCallbacks = useRef({});

  /** Registra uma seção e devolve a função de remoção. */
  const pvRegisterSection = useCallback((pId, pDefaultOpen) => {
    setSections((pSections) =>
      Object.prototype.hasOwnProperty.call(pSections, pId)
        ? pSections
        : {
            ...pSections,
            [pId]: { controlled: false, open: pDefaultOpen },
          }
    );

    return () => {
      delete xOpenChangeCallbacks.current[pId];
      setSections((pSections) =>
        Object.keys(pSections).reduce(
          (pRemainingSections, pSectionId) =>
            pSectionId === pId
              ? pRemainingSections
              : { ...pRemainingSections, [pSectionId]: pSections[pSectionId] },
          {}
        )
      );
    };
  }, []);

  /** Sincroniza o contrato controlado e o callback informado pela seção. */
  const pvSyncSection = useCallback(
    (pId, pControlled, pOpen, pOnOpenChange) => {
      xOpenChangeCallbacks.current[pId] = pOnOpenChange;
      setSections((pSections) => {
        const xSection = pSections[pId];

        if (!xSection) {
          return pSections;
        }

        const xNextOpen = pControlled ? pOpen : xSection.open;

        if (
          xSection.controlled === pControlled &&
          xSection.open === xNextOpen
        ) {
          return pSections;
        }

        return {
          ...pSections,
          [pId]: { controlled: pControlled, open: xNextOpen },
        };
      });
    },
    []
  );

  /** Atualiza a abertura de uma seção registrada. */
  const pvSetSectionOpen = useCallback(
    (pId, pOpen) => {
      const xSection = xSections[pId];

      if (!xSection) {
        return;
      }

      if (!xSection.controlled) {
        setSections((pSections) => ({
          ...pSections,
          [pId]: { ...pSections[pId], open: pOpen },
        }));
      }

      xOpenChangeCallbacks.current[pId]?.(pOpen);
    },
    [xSections]
  );

  /** Alterna simultaneamente todas as seções registradas. */
  const pvToggleAllSections = useCallback(() => {
    const xAllSectionsOpen = Object.values(xSections).every(
      (pSection) => pSection.open
    );
    const xNextOpen = !xAllSectionsOpen;

    setSections((pSections) =>
      Object.keys(pSections).reduce((pNextSections, pId) => {
        const xSection = pSections[pId];

        return {
          ...pNextSections,
          [pId]: xSection.controlled
            ? xSection
            : { ...xSection, open: xNextOpen },
        };
      }, {})
    );
    Object.keys(xSections).forEach((pId) =>
      xOpenChangeCallbacks.current[pId]?.(xNextOpen)
    );
  }, [xSections]);

  const xContextValue = useMemo(
    () => ({
      sections: xSections,
      registerSection: pvRegisterSection,
      syncSection: pvSyncSection,
      setSectionOpen: pvSetSectionOpen,
      toggleAllSections: pvToggleAllSections,
    }),
    [
      xSections,
      pvRegisterSection,
      pvSetSectionOpen,
      pvSyncSection,
      pvToggleAllSections,
    ]
  );

  return (
    <SearchFiltersSectionsContext.Provider value={xContextValue}>
      {children}
    </SearchFiltersSectionsContext.Provider>
  );
};

SearchFiltersSectionsProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

/** Disponibiliza o coordenador interno para as seções e para o cabeçalho. */
const useSearchFiltersSections = () => useContext(SearchFiltersSectionsContext);

/** Renderiza a ação coletiva somente quando o shell possui múltiplas seções. */
const SearchFiltersSectionsToggle = (pProps) => {
  const { align = "end" } = pProps;
  const xContext = useSearchFiltersSections();
  const xSectionValues = Object.values(xContext?.sections || {}).map(
    (pSection) => pSection.open
  );

  if (xSectionValues.length <= 1) {
    return null;
  }

  const xAllSectionsOpen = xSectionValues.every(Boolean);
  const xLabel = xAllSectionsOpen
    ? "Recolher todas as categorias"
    : "Expandir todas as categorias";

  return (
    <Tooltip title={xLabel}>
      <Box
        aria-label={xLabel}
        component="button"
        onClick={xContext.toggleAllSections}
        sx={{
          alignItems: "center",
          background: "transparent",
          border: 0,
          color: "primary.main",
          cursor: "pointer",
          display: "flex",
          font: "inherit",
          justifyContent: align === "end" ? "flex-end" : "flex-start",
          minHeight: 44,
          minWidth: 44,
          p: 0,
          "&:focus-visible": {
            outline: "3px solid",
            outlineColor: "primary.light",
            outlineOffset: 2,
          },
        }}
        type="button"
      >
        <Typography
          aria-hidden="true"
          color="primary"
          component="span"
          fontWeight={600}
          variant="body1"
        >
          {xAllSectionsOpen ? "−" : "+"}
        </Typography>
      </Box>
    </Tooltip>
  );
};

SearchFiltersSectionsToggle.propTypes = {
  align: PropTypes.oneOf(["start", "end"]),
};

export {
  SearchFiltersSectionsProvider,
  SearchFiltersSectionsToggle,
  useSearchFiltersSections,
};
